/**
 * A table of records, with a search box over it.
 *
 * WHY THE SEARCH IS HONEST ABOUT WHAT IT SEARCHES
 * -----------------------------------------------
 * None of these endpoints have a search of their own, so this filters what has
 * been loaded. Where that is everything, "search" is the right word. Where a
 * screen is showing a page, it is not — a person types a name, sees nothing,
 * and concludes the record does not exist.
 *
 * So the caller says which it is, and the label follows.
 */
import { useMemo, useState } from 'react';

import { Spinner } from './Spinner.jsx';

export function DataTable({
  rows,
  columns,
  loading,
  error,
  searchable,
  searchKeys = [],
  empty = 'Nothing here yet.',
  /** True when `rows` is everything there is, not a page of it. */
  complete = true,
  rowKey = (row) => row.id,
}) {
  const [term, setTerm] = useState('');

  const shown = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((row) =>
      searchKeys.some((key) =>
        String(row[key] ?? '')
          .toLowerCase()
          .includes(needle)
      )
    );
  }, [rows, term, searchKeys]);

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner label="Loading" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="py-12 text-center text-sm text-ink-2">
        Could not load this. Check the branch computer is running.
      </p>
    );
  }

  return (
    <div>
      {searchable && (
        <input
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={complete ? 'Search' : 'Find on this page'}
          aria-label={complete ? 'Search' : 'Find on this page'}
          className="mb-3 h-11 w-full max-w-sm rounded-lg border border-rule bg-surface-3 px-3.5 text-[15px] text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
        />
      )}

      {shown.length === 0 ? (
        <p className="py-12 text-center text-sm text-ink-2">
          {term ? `Nothing matching “${term}”.` : empty}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-rule">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-rule text-left text-xs uppercase tracking-wide text-ink-3">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`p-3 font-medium ${col.align === 'right' ? 'text-right' : ''}`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <tr key={rowKey(row)} className="border-b border-rule last:border-0">
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`p-3 ${col.align === 'right' ? 'text-right tabular-nums' : ''} ${
                        col.tone?.(row) ?? 'text-ink-2'
                      }`}
                    >
                      {col.render ? col.render(row) : (row[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {term && shown.length > 0 && (
        <p className="mt-2 text-xs text-ink-3">
          {shown.length} of {rows.length}
        </p>
      )}
    </div>
  );
}
